import { Button, type ButtonProps } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@components/IconButton';
import type { IconButtonProps } from '@components/IconButton/IconButton';

import { useSnackbar } from '@hooks/useSnackbar';

const messages = defineMessages({
  success: {
    id: 'GLOBAL.clipboardCopy',
    defaultMessage: 'Copied to clipboard',
  },
  error: {
    id: 'GLOBAL.clipboardCopyError',
    defaultMessage: 'Unable to copy content to clipboard',
  },
});

interface CopyButtonProps {
  /**
   * The content to be copied to the clipboard.
   */
  content: string;
  /**
   * Displayed component should be a clipboard icon or a button
   */
  variant?: 'button' | 'icon';
  /**
   * If button variant is used, displayed text of the button
   */
  buttonText?: string;
  buttonProps?: ButtonProps;
  iconButtonProps?: Omit<IconButtonProps, 'onClick'>;
}

/**
 * CopyButton component.
 * Renders an icon that, when clicked, copies the provided content to the clipboard.
 * Displays a success or error snackbar message upon completion.
 */
function CopyButton({
  buttonProps,
  buttonText,
  content,
  iconButtonProps,
  variant = 'icon',
}: CopyButtonProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  /**
   * Copies the provided content to the clipboard and displays a snackbar message.
   *
   * @param {string} text - The content to be copied to the clipboard.
   */
  const copyToClipboard = async (text: string) => {
    try {
      // Copy text to clipboard
      await navigator.clipboard.writeText(text);
      // Display success snackbar
      enqueueSnackbar({
        variant: 'success',
        message: formatMessage(messages.success),
      });
    } catch (err) {
      // Display error snackbar
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    }
  };

  const handleClick = async () => {
    await copyToClipboard(content);
  };

  return variant === 'icon' ? (
    <IconButton // Render a clipboard icon
      color="primary.main"
      name="clipboard"
      onClick={handleClick} // On click, copy content and display snackbar
      iconType="far"
      {...iconButtonProps}
    />
  ) : (
    <Button onClick={handleClick} variant="text" {...buttonProps}>
      {buttonText}
    </Button>
  );
}

export default CopyButton;
